import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import BriefNavbar from 'contest/components/brief-navbar';
import StickyElement from 'contest/components/sticky-element';
import ConnectedTranslationMenu from 'contest/components/connected/connected-translation-menu';
import ConnectedScoreSummary from 'contest/components/connected/connected-score-summary';

import ElementScrollTracker from 'contest/components/brief/element-scroll-tracker';
import Section from 'contest/components/brief/section';

import { ConnectedEditElementFrame } from 'contest/components/connected/connected-edit-element-frame';

import { ConnectedEditTextElement, ViewTextElement } from 'contest/components/brief/text-element';
import {
    ConnectedEditTextAreaElement,
    ViewTextAreaElement,
} from 'contest/components/brief/textarea-element';
import {
    ConnectedEditChoiceOtherElement,
    ConnectedEditChoiceElement,
    ViewChoiceOtherElement,
    ViewChoiceElement,
} from 'contest/components/brief/choice-other-element';
import {
    ConnectedEditRadioChoiceOtherElement,
    ConnectedEditRadioChoiceElement,
    ViewRadioChoiceOtherElement,
    ViewRadioChoiceElement,
} from 'contest/components/brief/radio-choice-other-element';
import {
    ConnectedEditImageChoiceOtherElement,
    ConnectedViewImageChoiceOtherElement,
} from 'contest/components/brief/image-choice-other-element';
import {
    ConnectedEditMultipleChoiceOtherElement,
    ConnectedEditMultipleChoiceElement,
    ConnectedViewMultipleChoiceOtherElement,
    ConnectedViewMultipleChoiceElement,
} from 'contest/components/brief/multiple-choice-other-element';
import {
    ConnectedEditSlidersElement,
    ViewSlidersElement,
} from 'contest/components/brief/sliders-element';
import {
    ConnectedEditMultipleChoiceImageElement,
    ViewMultipleChoiceImageElement,
} from 'contest/components/brief/multiple-choice-image-element';
import { ViewFilesElement } from 'contest/components/brief/files-element';
import {
    ConnectedEditMultipleChoiceDropdownElement,
    ConnectedViewMultipleChoiceDropdownElement,
} from 'contest/components/brief/multiple-choice-dropdown-element';
import {
    ConnectedEditWebsiteListElement,
    ConnectedViewWebsiteListElement,
} from 'contest/components/brief/website-list-element';

import { ConnectedHelpBoxSelector } from 'contest/components/brief/help-box';
import { ConnectedViewInfoBoxSelector } from 'contest/components/brief/view-info-box';

import ConnectedEditFilesElement from 'contest/components/connected/connected-edit-files-element';
import DeliverablesSection from 'contest/components/brief/deliverables-section';

import bugsnag from '@99designs/utils/platform/bugsnag';

const DELIVERABLES_KEY = 'deliverables';

export default class BriefApp extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            highlightedSection: false,
        };

        this.sectionPositions = {};

        this.onSectionChange = this.onSectionChange.bind(this);
        this.selectSection = this.selectSection.bind(this);
    }

    render() {
        return (
            <div className="sidebar-layout">
                {this.props.isExpandedUI && (
                    <div className="sidebar-layout__sidebar">{this.navbar()}</div>
                )}
                <div className="sidebar-layout__content">
                    {this.mobileTranslationMenu()}
                    {this.mobileScoreSummary()}
                    {this.content()}
                </div>
            </div>
        );
    }

    mobileTranslationMenu() {
        if (!this.props.isExpandedUI && this.props.translationMode) {
            return <ConnectedTranslationMenu />;
        }
    }

    mobileScoreSummary() {
        if (!this.props.isExpandedUI && this.hasBriefScore()) {
            return <ConnectedScoreSummary />;
        }
    }

    navbar() {
        const sections = Object.keys(this.props.outline).map(key => {
            const section = this.props.outline[key];
            return {
                key: key,
                title: section.title,
                isSelected: this.isSectionSelected(key),
                notifications: this.sectionChangeCount(key),
            };
        });

        if (this.props.deliverables.length) {
            sections.push({
                key: DELIVERABLES_KEY,
                title: <FormattedMessage id="brief_deliverables_navbar_label" />,
                isSelected: this.isSectionSelected('deliverables'),
            });
        }

        return (
            <StickyElement enabled={true} offsetTop={30}>
                <div className="brief-menu">
                    <div className="brief-menu__item">
                        <BriefNavbar sections={sections} onSelectSection={this.selectSection} />
                    </div>
                    {this.scoreSummary()}
                    {this.translationMenu()}
                </div>
            </StickyElement>
        );
    }

    scoreSummary() {
        if (this.hasBriefScore()) {
            return (
                <div className="brief-menu__item brief-menu__item--with-right-padding">
                    <ConnectedScoreSummary />
                </div>
            );
        }
    }

    translationMenu() {
        if (this.props.translationMode) {
            return (
                <div className="brief-menu__item brief-menu__item--with-left-padding brief-menu__item--with-right-padding">
                    <ConnectedTranslationMenu />
                </div>
            );
        }
    }

    content() {
        return (
            <div className="section--constrained">
                <ElementScrollTracker
                    onElementChange={this.onSectionChange}
                    isDisabled={!this.props.isExpandedUI}
                >
                    {this.sections()}
                </ElementScrollTracker>
            </div>
        );
    }

    sections() {
        const hasDeliverables = this.props.deliverables.length > 0;
        const sectionCount = Object.keys(this.props.outline).length;

        const sections = Object.keys(this.props.outline).map((sectionKey, i) => {
            const section = this.props.outline[sectionKey];
            const isLast = !hasDeliverables && i == sectionCount - 1;
            return (
                <Section
                    key={sectionKey}
                    id={sectionKey}
                    title={section.title}
                    innerRef={this.sectionRefHandler(sectionKey)}
                    isLast={isLast}
                >
                    {Object.keys(section.elements).map(elementKey => {
                        return this.element(sectionKey, elementKey, section.elements[elementKey]);
                    })}
                </Section>
            );
        });

        if (this.props.deliverables.length) {
            sections.push(
                <Section
                    key={DELIVERABLES_KEY}
                    id={DELIVERABLES_KEY}
                    innerRef={this.sectionRefHandler(DELIVERABLES_KEY)}
                    isLast={true}
                >
                    <DeliverablesSection
                        deliverables={this.props.deliverables}
                        fileTypes={this.props.fileTypes}
                        isCustomer={this.props.editMode}
                    />
                </Section>
            );
        }

        return sections;
    }

    element(sectionKey, elementKey, element) {
        return (
            <div
                id={`element-${sectionKey}-${elementKey}`}
                key={`element-${sectionKey}-${elementKey}`}
            >
                {this.props.editMode
                    ? this.elementEditView(sectionKey, elementKey, element)
                    : this.elementReadView(sectionKey, elementKey, element)}
            </div>
        );
    }

    getEditComponentForType(type) {
        switch (type) {
            case 'text':
                return ConnectedEditTextElement;
            case 'textarea':
                return ConnectedEditTextAreaElement;
            case 'choice':
                return ConnectedEditChoiceElement;
            case 'choiceOther':
                return ConnectedEditChoiceOtherElement;
            case 'radioChoice':
                return ConnectedEditRadioChoiceElement;
            case 'radioChoiceOther':
                return ConnectedEditRadioChoiceOtherElement;
            case 'imageChoiceOther':
                return ConnectedEditImageChoiceOtherElement;
            case 'multipleChoice':
                return ConnectedEditMultipleChoiceElement;
            case 'multipleChoiceOther':
                return ConnectedEditMultipleChoiceOtherElement;
            case 'multipleChoiceDropdown':
                return ConnectedEditMultipleChoiceDropdownElement;
            case 'sliders':
                return ConnectedEditSlidersElement;
            case 'multipleChoiceImage':
                return ConnectedEditMultipleChoiceImageElement;
            case 'files':
                return ConnectedEditFilesElement;
            case 'websiteList':
                return ConnectedEditWebsiteListElement;
            default:
                bugsnag.notify('BriefError', `Type ${type} has no associated edit element.`);
        }
    }

    elementEditView(sectionKey, elementKey, element) {
        const EditComponent = this.getEditComponentForType(element.type);
        if (EditComponent) {
            return (
                <ConnectedEditElementFrame
                    key={elementKey}
                    sectionKey={sectionKey}
                    elementKey={elementKey}
                >
                    <ConnectedHelpBoxSelector
                        sectionKey={sectionKey}
                        elementKey={elementKey}
                        listingId={this.props.listingId}
                    />
                    <EditComponent sectionKey={sectionKey} elementKey={elementKey} />
                </ConnectedEditElementFrame>
            );
        } else {
            return '';
        }
    }

    getReadComponentForType(type) {
        switch (type) {
            case 'text':
                return ViewTextElement;
            case 'textarea':
                return ViewTextAreaElement;
            case 'choice':
                return ViewChoiceElement;
            case 'choiceOther':
                return ViewChoiceOtherElement;
            case 'radioChoice':
                return ViewRadioChoiceElement;
            case 'radioChoiceOther':
                return ViewRadioChoiceOtherElement;
            case 'imageChoiceOther':
                return ConnectedViewImageChoiceOtherElement;
            case 'multipleChoice':
                return ConnectedViewMultipleChoiceElement;
            case 'multipleChoiceOther':
                return ConnectedViewMultipleChoiceOtherElement;
            case 'multipleChoiceDropdown':
                return ConnectedViewMultipleChoiceDropdownElement;
            case 'files':
                return ViewFilesElement;
            case 'sliders':
                return ViewSlidersElement;
            case 'multipleChoiceImage':
                return ViewMultipleChoiceImageElement;
            case 'websiteList':
                return ConnectedViewWebsiteListElement;
            default:
                bugsnag.notify('BriefError', `Type ${type} has no associated read element`);
        }
    }

    elementReadView(sectionKey, elementKey, element) {
        const ReadComponent = this.getReadComponentForType(element.type);
        const position = (element.infoBox && element.infoBox.position) || null;

        if (ReadComponent) {
            return (
                <div>
                    {position === 'before' ? (
                        <ConnectedViewInfoBoxSelector
                            sectionKey={sectionKey}
                            elementKey={elementKey}
                            listingId={this.props.listingId}
                        />
                    ) : null}
                    <ReadComponent
                        element={element}
                        hasChanged={this.elementHasChanged(sectionKey, elementKey)}
                        key={elementKey}
                    />
                    {position === 'after' ? (
                        <ConnectedViewInfoBoxSelector
                            sectionKey={sectionKey}
                            elementKey={elementKey}
                            listingId={this.props.listingId}
                        />
                    ) : null}
                </div>
            );
        } else {
            return '';
        }
    }

    sectionChangeCount(sectionKey) {
        return (
            (this.props.changes &&
                this.props.changes[sectionKey] &&
                Object.keys(this.props.changes[sectionKey]).length) ||
            0
        );
    }

    elementHasChanged(sectionKey, elementKey) {
        return Boolean(
            this.props.changes &&
                this.props.changes[sectionKey] &&
                this.props.changes[sectionKey][elementKey]
        );
    }

    isSectionSelected(key) {
        return this.state.highlightedSection == key;
    }

    onSectionChange(sectionKey) {
        this.setState({
            highlightedSection: sectionKey,
        });
    }

    sectionRefHandler(sectionKey) {
        return node => {
            if (node) {
                const rect = node.getBoundingClientRect();
                const scrollTop = Math.max(
                    window.pageYOffset,
                    document.documentElement.scrollTop,
                    document.body.scrollTop
                );
                const y = rect.top + scrollTop;
                this.sectionPositions[sectionKey] = y;
            } else {
                // React passes null ref on unmount
                delete this.sectionPositions[sectionKey];
            }
        };
    }

    selectSection(sectionKey) {
        if (this.sectionPositions[sectionKey]) {
            window.scrollTo(0, this.sectionPositions[sectionKey] - 30); // Scroll to 30px above target
        }
    }

    hasBriefScore() {
        return this.props.score && Object.keys(this.props.score).length > 0;
    }

    shouldComponentUpdate(nextProps, nextState) {
        return (
            this.props.outline !== nextProps.outline ||
            this.props.isExpandedUI !== nextProps.isExpandedUI ||
            this.state !== nextState
        );
    }

    componentDidMount() {
        if (window.location.hash !== '') {
            this.selectSection(window.location.hash.replace('#section-', ''));
        }
    }
}

BriefApp.propTypes = {
    listingId: PropTypes.number,
    outline: PropTypes.objectOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            elements: PropTypes.objectOf(
                PropTypes.shape({
                    type: PropTypes.string.isRequired,
                })
            ).isRequired,
        })
    ).isRequired,
    editMode: PropTypes.bool.isRequired,
    translationMode: PropTypes.bool.isRequired,
    changes: PropTypes.object,
    score: PropTypes.object,
    isExpandedUI: PropTypes.bool,
    deliverables: PropTypes.array.isRequired,
    fileTypes: PropTypes.array,
    createBriefFileUrl: PropTypes.string,
};

BriefApp.defaultProps = {
    fileTypes: [],
};



// WEBPACK FOOTER //
// ./src/web/js/contest/components/brief/app.jsx